<template>
  <div>
    <v-bottom-sheet location="right end" v-model="approvalSheet" width="60%">
      <v-card rounded="t-lg">
        <v-card-title class="d-flex align-center justify-space-between">
          <span><v-icon size="small" class="mr-2" icon="mdi-history"></v-icon>Approval History</span>
          <v-btn variant="tonal" color="red-darken-3"  @click="approvalSheet = false">Close</v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pa-4">
          <v-alert v-if="approvalHistory.length === 0" type="info" variant="tonal">
            No approval action has been recorded yet.
          </v-alert>

          <v-timeline
              v-else
              align="center"
              density="comfortable"
              side="end"
              line-color="yellow-darken-3"
              line-thickness="2"

          >
            <v-timeline-item
                v-for="(item, index) in approvalHistory"
                :key="item.id"
                :line-inset="10"
                :dot-color="statusColor(item.status)"
                :icon="statusIcon(item.status)"
                icon-color="white"
                size="small"
            >

              <!-- LEFT side: details card for even (1st, 3rd...), chip for odd (2nd, 4th...) -->
              <template v-if="index % 2 === 0" #opposite>
                <v-card style="width:300px" variant="tonal" :color="statusColor(item.status)">
                  <v-card-item class="pb-2">
                    <template #prepend>
                      <v-icon :color="statusColor(item.status)" class="mr-2">
                        {{ statusIcon(item.status) }}
                      </v-icon>
                    </template>
                    <v-card-title class="text-body-1 font-weight-bold px-0">
                      {{ actorName(item) }}
                    </v-card-title>
                    <v-card-subtitle class="px-0">
                      {{ stepName(item) }}
                    </v-card-subtitle>
                    <template #append>
                      <v-chip :color="statusColor(item.status)" size="small" variant="flat">
                        {{ statusLabel(item.status) }}
                      </v-chip>
                    </template>
                  </v-card-item>

                  <v-card-text v-if="item.comments" class="pt-0 text-body-2">
                    <span class="font-weight-medium">Comment:</span> {{ item.comments }}
                  </v-card-text>
                </v-card>
              </template>
              <template v-else #opposite>
                <v-chip
                    variant="tonal"
                    :color="statusColor(item.status)"
                    class="text-caption text-medium-emphasis text-no-wrap"
                >
                  {{ formatDate(item.approved_at) }}
                </v-chip>
              </template>

              <!-- RIGHT side: chip for even (1st, 3rd...), details card for odd (2nd, 4th...) -->
              <template v-if="index % 2 === 0" #default>
                <v-chip
                    variant="tonal"
                    :color="statusColor(item.status)"
                    class="text-caption text-medium-emphasis text-no-wrap"
                >
                  {{ formatDate(item.approved_at) }}
                </v-chip>
              </template>
              <template v-else #default>
                <v-card style="width:300px" variant="tonal" :color="statusColor(item.status)">
                  <v-card-item class="pb-2">
                    <template #prepend>
                      <v-icon :color="statusColor(item.status)" class="mr-2">
                        {{ statusIcon(item.status) }}
                      </v-icon>
                    </template>
                    <v-card-title class="text-body-1 font-weight-bold px-0">
                      {{ actorName(item) }}
                    </v-card-title>
                    <v-card-subtitle class="px-0">
                      {{ stepName(item) }}
                    </v-card-subtitle>
                    <template #append>
                      <v-chip :color="statusColor(item.status)" size="small" variant="flat">
                        {{ statusLabel(item.status) }}
                      </v-chip>
                    </template>
                  </v-card-item>

                  <v-card-text v-if="item.comments" class="pt-0 text-body-2">
                    <span class="font-weight-medium">Comment:</span> {{ item.comments }}
                  </v-card-text>
                </v-card>
              </template>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>

        <v-divider />
        <v-card-actions v-if="canApprove">
          <v-spacer />
          <v-btn
              v-if="this.approvalDetails.is_forward && !this.currentApprovalStep?.is_final_step"
              color="warning"
              prepend-icon="mdi-share"
              :disabled="approvalActionLoading"
              @click="openApprovalDialog('Forwarded')"
          >
            Forward
          </v-btn>
          <v-btn
              color="success"
              variant="flat"
              prepend-icon="mdi-check"
              :disabled="approvalActionLoading"
              @click="openApprovalDialog('Approved')"
          >
            Approve
          </v-btn>
          <v-btn
              color="error"
              variant="outlined"
              prepend-icon="mdi-close-octagon-outline"
              :disabled="approvalActionLoading"
              @click="openApprovalDialog('Cancelled')"
          >
            Reject
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-bottom-sheet>

    <v-dialog v-model="approvalDialog" max-width="500">
      <v-card>
        <v-card-title>{{ approvalActionTitle }}</v-card-title>
        <v-divider />
        <v-card-text class="pa-3">
          <v-textarea
            v-model="approvalComment"
            label="Comments (Optional)"
            placeholder="Write your comments here..."
            rows="3"
            hide-details
            variant="outlined"
            maxlength="225"
            counter
            :error-messages="commentError"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn variant="tonal" color="red" @click="approvalDialog = false">Cancel</v-btn>
          <v-btn
              color="primary"
              variant="flat"
              :loading="approvalActionLoading"
              @click="confirmApprovalAction"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Axios from "axios";
import { toast } from "vue3-toastify";
import { updateApprovalStatus as updateApprovalStatusApi } from "@/store/api";

export default {
  props: {
    approvalDetails: {
      type: Object,
      default: () => ({}),
    },
    approvalStatus: {
      type: String,
      default: "",
    },
    subject: {
      type: String,
      default: "Request",
    },
  },

  emits: ["updated"],

  data: () => ({
    approvalSheet: false,
    approvalDialog: false,
    selectedApprovalAction: "",
    approvalComment: "",
    commentError: "",
    loadingApprove: false,
    loadingReject: false,
    loadingForward: false,
  }),

  computed: {
    approvalHistory() {
      const history = this.rawApprovalHistory;
      const approvedIndex = history.findIndex(
          (approval) => approval.status?.toLowerCase() === "approved" || "cancelled"
      );

      if (!this.isForwardApprovalComplete || approvedIndex === -1) return history;

      return history.map((approval, index) => ({
        ...approval,
        status: index > approvedIndex && approval.status?.toLowerCase() === "pending"
            ? "skipped"
            : approval.status,
      }));
    },
    rawApprovalHistory() {
      return this.approvalDetails.approval_process
          || this.approvalDetails.data
          || [];
    },
    isForwardApprovalComplete() {
      return (
        (Number(this.approvalDetails.is_forward) === 1 && this.approvalStatus.toLowerCase() === "approved")
        || this.approvalStatus.toLowerCase() === "cancelled"
      );
    },
    currentApprovalStep() {
      if (this.approvalStatus?.toLowerCase() !== "pending") return null;

      return this.rawApprovalHistory.find(
          (approval) => approval.status?.toLowerCase() === "pending"
      ) || null;
    },
    canApprove() {
      return Boolean(
          this.currentApprovalStep
          && this.currentApprovalStep.approval_permission === true
          && Number(this.currentApprovalStep.employee_id) === Number(this.$store.getters.getAuthUser?.employee?.id)
      );
    },
    approvalActionLoading() {
      return this.loadingApprove || this.loadingReject || this.loadingForward;
    },
    approvalActionTitle() {
      return {
        Approved: `Approve ${this.subject}`,
        Forwarded: `Forward ${this.subject}`,
        Cancelled: `Reject ${this.subject}`,
      }[this.selectedApprovalAction] || "Approval Action";
    },
  },

  watch: {
    approvalComment() {
      if (this.commentError && this.approvalComment.length <= 225) this.commentError = "";
    },
  },

  methods: {
    open() {
      this.approvalSheet = true;
    },
    openApprovalDialog(action) {
      this.selectedApprovalAction = action;
      this.approvalComment = "";
      this.commentError = "";
      this.approvalSheet = false;
      this.$nextTick(() => {
        this.approvalDialog = true;
      });
    },
    async confirmApprovalAction() {
      if (!this.currentApprovalStep || !this.canApprove) {
        toast.error("You are not authorized to perform this approval action");
        return;
      }
      if (this.approvalComment.length > 225) {
        this.commentError = "Comment cannot exceed 225 characters";
        return;
      }

      const loadingKey = {
        Approved: "loadingApprove",
        Forwarded: "loadingForward",
        Cancelled: "loadingReject",
      }[this.selectedApprovalAction];
      this[loadingKey] = true;

      try {
        const response = await Axios.post(
            `${updateApprovalStatusApi}/${this.currentApprovalStep.id}`,
            { comments: this.approvalComment || "", status: this.selectedApprovalAction },
            { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (!response.data.success) {
          throw new Error(response.data.message || "Approval action failed");
        }

        const actionLabel = {
          Approved: "Approved",
          Forwarded: "Forwarded",
          Cancelled: "Rejected",
        }[this.selectedApprovalAction] || "Updated";
        toast.success(`${actionLabel} successfully`);
        this.approvalDialog = false;
        this.approvalSheet = true;
        this.$emit("updated");
      } catch (error) {
        toast.error(error.response?.data?.message || error.message || "Approval action failed");
      } finally {
        this[loadingKey] = false;
      }
    },
    formatDate(value) {
      if (!value) return "Pending";

      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
    },
    normalizedStatus(status) {
      return status?.toLowerCase() || "pending";
    },
    statusLabel(status) {
      return {
        approved: "Approved",
        cancelled: "Rejected",
        rejected: "Rejected",
        forwarded: "Forwarded",
        pending: "Pending",
        skipped: "Skipped",
      }[this.normalizedStatus(status)] || "Pending";
    },
    actorName(item) {
      return item.approved_by?.full_name
          || item.employee?.name
          || "Waiting for approver";
    },
    stepName(item) {
      return item.approval_step_name?.name || "Approver";
    },
    // approvalTitle(item) {
    //   return {
    //     approved: "Approval completed",
    //     cancelled: "Approval rejected",
    //     rejected: "Approval rejected",
    //     forwarded: "Approval forwarded",
    //     pending: "Approval pending",
    //     skipped: "Approval skipped",
    //   }[this.normalizedStatus(item.status)] || "Approval pending";
    // },
    statusColor(status) {
      return {
        approved: "success",
        pending: "warning",
        forwarded: "info",
        cancelled: "error",
        rejected: "error",
        skipped: "grey",
      }[this.normalizedStatus(status)] || "grey";
    },
    statusIcon(status) {
      return {
        approved: "mdi-check-circle-outline",
        pending: "mdi-clock-outline",
        forwarded: "mdi-share-outline",
        cancelled: "mdi-close-circle-outline",
        rejected: "mdi-close-circle-outline",
        skipped: "mdi-minus-circle-outline",
      }[this.normalizedStatus(status)] || "mdi-help-circle-outline";
    },
  },
};
</script>